/**
 * User Menu Process
 * 
 * Displays user menu dropdown with profile, settings, subscription, and logout options.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { getUserData, isAuthenticated } from '../../services/auth/token-storage.js';
import { 
  designTokens, 
  createButton,
  createContainer
} from '../../core/design-system.js';

export class UserMenuProcess extends BaseProcess {
  kind: string = 'user-menu';

  metadata = {
    priority: ProcessPriority.SYSTEM,
    category: 'system' as const,
    canStop: false,
    preserveState: true,
    timeSensitive: false
  };

  private userMenuView: UserMenuView | null = null;

  constructor(id: string) {
    super(id);
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[UserMenuProcess] Starting user menu process ${this.id}`);
    
    // Check auth state after a short delay to allow auth process to load state
    // The auth process loads state asynchronously, so we need to wait a bit
    setTimeout(() => {
      if (isAuthenticated()) {
        this.showUserMenu(ctx);
      }
    }, 500);
    
    // Listen for auth state changes
    bus.on('auth.login.success', () => {
      this.showUserMenu(ctx);
    });
    
    bus.on('auth.logout.success', () => {
      this.hideUserMenu(ctx);
    });
    
    // Also listen for when auth state is loaded (in case user was already logged in)
    bus.on('auth.state.loaded', () => {
      if (isAuthenticated()) {
        this.showUserMenu(ctx);
      }
    });
    
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  private showUserMenu(ctx: ProcessContext): void {
    if (this.userMenuView) {
      // Already showing, update it
      this.userMenuView.updateUserData();
      return;
    }
    
    const view = new UserMenuView(
      this.id,
      `${this.id}-main`,
      this
    );
    const viewInstance = view.toViewInstance();
    this.userMenuView = view;
    
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);
  }

  private hideUserMenu(ctx: ProcessContext): void {
    if (this.userMenuView) {
      ctx.destroyView(this.userMenuView.id);
      this.views.delete(this.userMenuView.id);
      this.userMenuView = null;
    }
  }
}

/**
 * User Menu View
 * 
 * Displays user menu as a fixed-position dropdown in top-right corner.
 */
class UserMenuView extends BaseViewHTML {
  private isOpen: boolean = false;

  constructor(
    processId: string,
    viewId: string,
    private process: UserMenuProcess
  ) {
    super(processId, viewId, {
      sizes: [1], // Full height, normal width
      showDismissButton: false,
      resizable: false,
      isOverlay: true // Fixed position overlay, not part of layout
    });
  }

  protected getContentHTML(): string {
    const userData = getUserData();
    const displayName = userData?.displayName || userData?.email || 'User';
    const email = userData?.email || '';
    
    // Menu button (always visible)
    const menuButton = `
      <button class="user-menu-button" style="
        position: fixed;
        top: 20px;
        right: 20px;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: ${designTokens.cardBackground};
        border: 1px solid ${designTokens.borderColor};
        color: ${designTokens.contentColor};
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${designTokens.fontSize.lg};
        font-weight: 600;
        z-index: 1001;
        transition: all 0.2s;
      " onmouseover="this.style.background='rgba(255,255,255,0.15)'" onmouseout="this.style.background='${designTokens.cardBackground}'">
        ${this.escapeHtml(displayName.charAt(0).toUpperCase())}
      </button>
    `;
    
    // Dropdown menu (shown when open)
    const dropdownMenu = this.isOpen ? `
      <div class="user-menu-dropdown" style="
        position: fixed;
        top: 78px;
        right: 20px;
        width: 240px;
        background: ${designTokens.cardBackground};
        border: 1px solid ${designTokens.borderColor};
        border-radius: ${designTokens.radius.md};
        padding: ${designTokens.padding.md};
        z-index: 1002;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      ">
        <div style="
          padding: ${designTokens.padding.md} 0;
          border-bottom: 1px solid ${designTokens.borderColor};
          margin-bottom: ${designTokens.padding.md};
        ">
          <div style="
            font-weight: 600;
            color: ${designTokens.contentColor};
            font-size: ${designTokens.fontSize.md};
            margin-bottom: ${designTokens.padding.xs};
          ">${this.escapeHtml(displayName)}</div>
          <div style="
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.sm};
          ">${this.escapeHtml(email)}</div>
        </div>
        <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xs};">
          ${createButton('Settings', {
            className: 'user-menu-settings',
            variant: 'ghost',
            style: 'text-align: left; justify-content: flex-start;'
          })}
          ${createButton('Subscription', {
            className: 'user-menu-subscription',
            variant: 'ghost',
            style: 'text-align: left; justify-content: flex-start;'
          })}
          ${createButton('Pricing', {
            className: 'user-menu-pricing',
            variant: 'ghost',
            style: 'text-align: left; justify-content: flex-start;'
          })}
          ${createButton('Decision History', {
            className: 'user-menu-history',
            variant: 'ghost',
            style: 'text-align: left; justify-content: flex-start;'
          })}
          <div style="
            margin-top: ${designTokens.padding.sm};
            padding-top: ${designTokens.padding.sm};
            border-top: 1px solid ${designTokens.borderColor};
          ">
            ${createButton('Logout', {
              className: 'user-menu-logout',
              variant: 'ghost',
              style: 'text-align: left; justify-content: flex-start; color: rgba(255, 0, 0, 0.8);'
            })}
          </div>
        </div>
      </div>
    ` : '';
    
    return menuButton + dropdownMenu;
  }

  protected render(): void {
    super.render();
    
    if (!this.element) return;
    
    // Override BaseViewHTML styles to be transparent overlay
    // This prevents the "empty gray box" issue
    this.element.style.backgroundColor = 'transparent';
    this.element.style.border = 'none';
    this.element.style.boxShadow = 'none';
    this.element.style.pointerEvents = 'none'; // Allow clicking through the empty space
    this.element.style.overflow = 'visible'; // Allow dropdown to spill out
    this.element.style.borderRadius = '0';

    // Setup menu button toggle
    const menuButton = this.element.querySelector('.user-menu-button') as HTMLButtonElement;
    if (menuButton) {
      // Ensure button is clickable even though container is pointer-events: none
      menuButton.style.pointerEvents = 'auto';
      
      menuButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleMenu();
      });
    }
    
    // Ensure dropdown is clickable
    const dropdown = this.element.querySelector('.user-menu-dropdown') as HTMLElement;
    if (dropdown) {
      dropdown.style.pointerEvents = 'auto';
    }
    
    // Setup menu item handlers
    const settingsBtn = this.element.querySelector('.user-menu-settings') as HTMLButtonElement;
    const subscriptionBtn = this.element.querySelector('.user-menu-subscription') as HTMLButtonElement;
    const pricingBtn = this.element.querySelector('.user-menu-pricing') as HTMLButtonElement;
    const historyBtn = this.element.querySelector('.user-menu-history') as HTMLButtonElement;
    const logoutBtn = this.element.querySelector('.user-menu-logout') as HTMLButtonElement;
    
    if (settingsBtn) {
      settingsBtn.addEventListener('click', () => {
        this.closeMenu();
        bus.emit('settings.show', {});
      });
    }
    
    if (subscriptionBtn) {
      subscriptionBtn.addEventListener('click', () => {
        this.closeMenu();
        bus.emit('subscription.show', {});
      });
    }
    
    if (pricingBtn) {
      pricingBtn.addEventListener('click', () => {
        this.closeMenu();
        window.location.href = '/pricing';
      });
    }
    
    if (historyBtn) {
      historyBtn.addEventListener('click', () => {
        this.closeMenu();
        bus.emit('decision-history.show', {});
      });
    }
    
    if (logoutBtn) {
      logoutBtn.addEventListener('click', () => {
        this.closeMenu();
        bus.emit('auth.logout', {});
      });
    }
    
    // Close menu when clicking outside
    document.addEventListener('click', (e) => {
      if (this.isOpen && this.element && !this.element.contains(e.target as Node)) {
        this.closeMenu();
      }
    });
  }

  private toggleMenu(): void {
    this.isOpen = !this.isOpen;
    this.markDirty();
  }

  private closeMenu(): void {
    this.isOpen = false;
    this.markDirty();
  }

  updateUserData(): void {
    this.markDirty();
  }
}

registerProcess('user-menu', (id: string) => new UserMenuProcess(id));

